import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, AlertTriangle, ShieldAlert } from 'lucide-react';
import CollegeHeader from './CollegeHeader';
import CollegeHomeSideBar from './CollegeHomeSideBar';

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  border: '1px solid var(--border-subtle, #E2E8F0)',
  radius: { sm: '8px', md: '10px', lg: '14px', xl: '18px', full: '999px' },
  shadow: { xs: '0 1px 3px rgba(0,0,0,0.06)', sm: '0 2px 8px rgba(0,0,0,0.08)', lg: '0 8px 32px rgba(0,0,0,0.10)' },
};

// ─── Main component ───────────────────────────────────────────────────────────
const LogoutPrompt = ({ onCancel, onConfirm }) => {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  const handleCancel = () => {
    if (onCancel) { onCancel(); return; }
    navigate(-1);
  };

  const handleLogout = async () => {
    if (onConfirm) { onConfirm(); return; }
    setConfirming(true);
    await new Promise(r => setTimeout(r, 600));
    localStorage.clear();
    navigate('/CollegeLogin', { replace: true });
  };

  return (
    <div className="college-main-layout" style={{ background: '#F8FAFC' }}>
      <CollegeHeader />

      <div className="college-content-wrapper">
        <CollegeHomeSideBar />

        <main className="college-main-content" style={{ padding: '32px 28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: '100%', maxWidth: 480 }}>

            {/* Confirmation card */}
            <div style={{
              background: '#FFFFFF', borderRadius: T.radius.xl,
              border: '1px solid #FECACA', boxShadow: '0 8px 40px rgba(220,38,38,0.10)',
              overflow: 'hidden', position: 'relative',
            }}>
              {/* 4px accent strip */}
              <div style={{ height: 4, background: 'linear-gradient(90deg, #DC2626, #EA580C)', flexShrink: 0 }} />

              <div style={{ padding: '36px 36px 32px' }}>

                {/* Icon circle */}
                <div style={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #FEE2E2, #FECACA)',
                  border: '2px solid #FECACA',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 20px',
                }}>
                  <ShieldAlert size={30} style={{ color: '#DC2626' }} />
                </div>

                {/* Title */}
                <h2 style={{
                  fontSize: '1.375rem', fontWeight: 800, color: '#0F172A',
                  textAlign: 'center', margin: '0 0 10px', letterSpacing: '-0.02em',
                }}>
                  Are you sure?
                </h2>

                {/* Subtitle */}
                <p style={{
                  fontSize: '0.875rem', color: '#64748B', textAlign: 'center',
                  lineHeight: 1.7, margin: '0 0 24px',
                }}>
                  You are about to log out from <strong style={{ color: '#374151' }}>this device</strong>.
                  Any unsaved changes will be lost.
                </p>

                {/* Warning banner */}
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '12px 14px', borderRadius: T.radius.md,
                  background: '#FFF7ED', border: '1px solid #FED7AA',
                  marginBottom: 28,
                }}>
                  <AlertTriangle size={16} style={{ color: '#D97706', flexShrink: 0, marginTop: 1 }} />
                  <p style={{ fontSize: '0.8125rem', color: '#92400E', margin: 0, lineHeight: 1.6 }}>
                    Your current session on this device will end immediately. You will need to log in again to continue.
                  </p>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    onClick={handleCancel}
                    disabled={confirming}
                    style={{
                      flex: 1, padding: '12px 0',
                      background: '#F8FAFC', border: T.border,
                      borderRadius: T.radius.md, cursor: confirming ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem', fontWeight: 700, color: '#374151',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { if (!confirming) { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.borderColor = '#CBD5E1'; } }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#F8FAFC'; e.currentTarget.style.borderColor = '#E2E8F0'; }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLogout}
                    disabled={confirming}
                    style={{
                      flex: 1, padding: '12px 0',
                      background: confirming ? '#FCA5A5' : 'linear-gradient(135deg, #DC2626, #B91C1C)',
                      border: 'none', borderRadius: T.radius.md,
                      cursor: confirming ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem', fontWeight: 700, color: '#FFFFFF',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      boxShadow: confirming ? 'none' : '0 4px 12px rgba(220,38,38,0.35)',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { if (!confirming) e.currentTarget.style.boxShadow = '0 6px 18px rgba(220,38,38,0.45)'; }}
                    onMouseLeave={e => { if (!confirming) e.currentTarget.style.boxShadow = '0 4px 12px rgba(220,38,38,0.35)'; }}
                  >
                    {confirming
                      ? <><span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Logging out…</>
                      : <><LogOut size={15} /> Logout</>
                    }
                  </button>
                </div>

              </div>
            </div>

            {/* Helper note */}
            <p style={{ textAlign: 'center', fontSize: '0.78rem', color: '#94A3B8', marginTop: 16 }}>
              Only this device's session will be ended.
            </p>

          </div>
        </main>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default LogoutPrompt;
